import * as stylex from '@stylexjs/stylex';
import { useState } from 'react';
import { TextField } from '@seeqdev/qomponents';
import type { FormControlElement } from '@seeqdev/qomponents';

/**
 * Text input field with a default value and an edit mode to avoid overwriting the
 * value typed by the user. This is useful for formatted number inputs.
 */
interface InputWithFormatterProps<T> {
  /** value to display in the input box prior to editing */
  value: T;
  /** text to set for the "data-testid" property */
  testId: string;
  /** class name(s) for the FormControl component */
  inputClassName?: string;
  xstyle?: stylex.StyleXStyles;
  /** type of the input (e.g. "number") */
  inputType: 'number' | 'text' | 'password';
  /** action that takes an input (same type as value) and returns a string with the desired formatting applied */
  formatter: (inputValue: T) => string;
  /** action to take when the input value changes (i.e. the user types into the field) */
  onChange: React.ChangeEventHandler<FormControlElement>;
  maxLength?: number;
  isInvalid?: boolean;
}

export const InputWithFormatter = <T,>(props: InputWithFormatterProps<T>) => {
  const { value, testId, inputClassName, xstyle, inputType, formatter, onChange, maxLength, isInvalid } = props;

  const [isEditing, setIsEditing] = useState(false);

  return (
    <TextField
      extraClassNames={inputClassName}
      xstyle={[styles.input, xstyle]}
      name={testId}
      type={inputType}
      size="sm"
      value={isEditing ? undefined : formatter(value)}
      onChange={onChange}
      onFocus={(event) => {
        event.target.select();
        setIsEditing(true);
      }}
      onBlur={() => setIsEditing(false)}
      testId={testId}
      showError={isInvalid}
      maxLength={maxLength}
    />
  );
};

const styles = stylex.create({ input: { width: '100%' } });
